import { useContext, useEffect, useMemo, useState } from 'react';

import { Formik } from 'formik';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';

import { EventContext } from '../../Contexts/EventContext';
import Loading from '../../Shared/Components/Loading';
import { error, success } from '../../Shared/Components/Toast';
import useApi from '../../Shared/hooks/api/Index';
import { usePasswordPolicy } from '../../Shared/hooks/usePasswordPolicy';
import {
  getApiErrorMessage,
  getApiErrorStatus,
  serverErrorHandler,
} from '../../Shared/utils/helper';
import Master from '../Layouts/Master';
import UserProfileFormFields from './UserProfileFormFields';
import {
  buildProfileInitialValues,
  buildProfileValidationSchema,
  prepareProfilePayload,
} from './profileFormShared';

const Update = () => {
  const passwordPolicy = usePasswordPolicy();
  const validationSchema = useMemo(
    () => buildProfileValidationSchema(passwordPolicy),
    [passwordPolicy],
  );

  const navigate = useNavigate();
  const { id } = useParams();
  const eventContext = useContext(EventContext);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [photoIds, setPhotoIds] = useState([]);
  const [user, setUser] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    await useApi.get(
      '/province',
      {},
      (res) => {
        const provinces = [...res.data.data];
        const last = provinces?.pop();
        provinces?.unshift(last);
        eventContext.provinces = provinces;
      },
      () => error('خطا در دریافت استان‌ها'),
    );
    await useApi.get(
      `/user/show/${id}`,
      {},
      (res) => {
        setUser(res.data.data);
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        error(getApiErrorMessage(err, 'خطا در دریافت اطلاعات کاربر'));
      },
    );
  };

  const onSubmit = async (values, { setSubmitting }) => {
    const payload = prepareProfilePayload(values, photoIds);

    setSubmitLoading(true);
    await useApi.post(
      `/user/register/${id}`,
      payload,
      (res) => {
        setUser(res.data.data);
        success('کاربر با موفقیت ویرایش شد');
        setSubmitLoading(false);
        setSubmitting(false);
        navigate('/panel/user', { replace: true });
      },
      (err) => {
        setSubmitLoading(false);
        setSubmitting(false);
        if (getApiErrorStatus(err) === 422) {
          serverErrorHandler(err?.data?.errors, () => {});
          error(getApiErrorMessage(err, 'اطلاعات وارد شده نامعتبر است'));
        } else {
          error('خطا در ذخیره اطلاعات کاربر');
        }
      },
    );
  };

  if (loading || !user) {
    return <Loading />;
  }

  return (
    <Master>
      <div className='d-flex flex-row justify-content-between border-bottom pb-2 my-3'>
        <div className='d-flex flex-row mt-2'>
          <i className='fad fa-user-edit f-20 text-secondary mx-2'></i>
          <h1 className='text-secondary f-13 font-weight-bold mt-1 mb-0 p-0'>
            ویرایش کاربر
          </h1>
        </div>
      </div>
      <Formik
        enableReinitialize
        initialValues={buildProfileInitialValues(user)}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({
          values,
          errors,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <UserProfileFormFields
              values={values}
              errors={errors}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
              provinces={eventContext.provinces}
              photoIds={photoIds}
              setPhotoIds={setPhotoIds}
            />
            <div className='col-12 d-flex justify-content-end my-3'>
              <button
                type='submit'
                className='btn btn-info'
                disabled={isSubmitting || submitLoading}
              >
                {submitLoading ? (
                  <span
                    className='spinner-border spinner-border-sm'
                    role='status'
                  />
                ) : (
                  'ذخیره تغییرات'
                )}
              </button>
            </div>
          </form>
        )}
      </Formik>
    </Master>
  );
};

export default Update;
